import { useState, useEffect } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, X, MessageCircle, Mail, ArrowUpRight } from "lucide-react";
import { SITE, NAV_LINKS, whatsappLink } from "../content";

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-neutral-200" data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center" data-testid="nav-logo-link">
          <img
            src={`${process.env.PUBLIC_URL}/assets/logo-new.png`}
            alt="The Next Design, Attract. Engage. Convert."
            className="h-14 md:h-16 w-auto object-contain"
            data-testid="nav-logo"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `link-underline text-[12px] font-medium uppercase tracking-[0.16em] transition-colors ${
                  isActive ? "text-brand-red" : "text-neutral-800 hover:text-brand-red"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <span className="h-5 w-px bg-neutral-300" aria-hidden="true" />
          <a
            href={whatsappLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-whatsapp-cta"
            className="bg-brand-red hover:bg-black text-white text-[12px] font-medium uppercase tracking-[0.14em] px-5 py-3 transition-colors flex items-center gap-2"
          >
            <MessageCircle size={14} strokeWidth={1.75} />
            Talk to Nitin
          </a>
          <a
            href={`mailto:${SITE.email}`}
            data-testid="nav-email-link"
            className="text-[12px] font-medium uppercase tracking-[0.14em] text-neutral-800 hover:text-brand-red transition-colors flex items-center gap-2"
          >
            <Mail size={14} strokeWidth={1.75} />
            Email Nitin
          </a>
        </nav>

        <button
          className="lg:hidden p-2"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
        </button>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-neutral-200 bg-white" data-testid="nav-mobile">
          <div className="px-6 py-8 flex flex-col gap-6">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm font-medium uppercase tracking-[0.2em] ${isActive ? "text-brand-red" : "text-neutral-900"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={whatsappLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-mobile-whatsapp-cta"
              className="bg-brand-red text-white text-[13px] font-medium uppercase tracking-[0.15em] px-6 py-4 text-center mt-2"
            >
              Talk to Nitin on WhatsApp
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="bg-neutral-950 text-white" data-testid="site-footer">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-20 grid grid-cols-1 md:grid-cols-12 gap-12">
      <div className="md:col-span-5">
        <div className="bg-white inline-block px-5 py-4">
          <img
            src={`${process.env.PUBLIC_URL}/assets/logo-new.png`}
            alt="The Next Design, Attract. Engage. Convert."
            className="h-12 w-auto object-contain"
            data-testid="footer-logo"
          />
        </div>
        <p className="mt-6 text-neutral-400 font-light leading-relaxed max-w-sm">
          A strategic creative partner for manufacturers with multiple factories, products and export markets.
        </p>
      </div>
      <div className="md:col-span-3">
        <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-neutral-500 mb-6">Explore</p>
        <div className="flex flex-col gap-3.5">
          {NAV_LINKS.map((l) => (
            <Link key={l.to} to={l.to} className="text-neutral-300 hover:text-white transition-colors text-sm font-light" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
              {l.label}
            </Link>
          ))}
        </div>
      </div>
      <div className="md:col-span-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-neutral-500 mb-6">Start a Conversation</p>
        <div className="flex flex-col gap-3.5 text-sm font-light">
          <a href={whatsappLink()} target="_blank" rel="noopener noreferrer" className="text-neutral-300 hover:text-white flex items-center gap-2 transition-colors" data-testid="footer-whatsapp-link">
            WhatsApp Nitin <ArrowUpRight size={14} strokeWidth={1.5} />
          </a>
          <a href={`mailto:${SITE.email}`} className="text-neutral-300 hover:text-white flex items-center gap-2 transition-colors" data-testid="footer-email-link">
            {SITE.email} <ArrowUpRight size={14} strokeWidth={1.5} />
          </a>
          <p className="text-neutral-500 mt-2">Serving manufacturers across India and export markets.</p>
        </div>
      </div>
    </div>
    <div className="border-t border-neutral-800">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row justify-between gap-3 text-[11px] text-neutral-500 uppercase tracking-[0.25em]">
        <span>© {new Date().getFullYear()} The Next Design</span>
        <span className="text-brand-red font-medium">Attract. Engage. Convert.</span>
      </div>
    </div>
  </footer>
);

const FloatingWhatsApp = () => (
  <a
    href={whatsappLink()}
    target="_blank"
    rel="noopener noreferrer"
    aria-label="WhatsApp Nitin"
    data-testid="floating-whatsapp-button"
    className="fixed bottom-6 right-6 z-50 bg-brand-red hover:bg-black text-white p-4 transition-colors flex items-center gap-2"
  >
    <MessageCircle size={20} strokeWidth={1.5} />
    <span className="hidden md:inline text-[11px] font-medium uppercase tracking-[0.2em]">WhatsApp Nitin</span>
  </a>
);

export default function Layout() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-neutral-900">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
